import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { navigation, NavGroup } from '../../../core/navigation/navigation';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'dime-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatIconModule, MatTooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  public collapsed = input(false);

  /** Roles del usuario autenticado (obtenidos de Keycloak) */
  private readonly _userRoles = signal<string[]>([]);

  /** Conjunto de títulos de items expandidos */
  private readonly _openItems = signal<Set<string>>(new Set());

  private readonly keycloakService = inject(KeycloakService);

  /**
   * Grupos de navegación filtrados según los roles del usuario.
   * - Un grupo/item SIN roles definidos es visible para todos los autenticados.
   * - Un grupo/item CON roles es visible solo si el usuario posee al menos uno.
   */
  readonly filteredGroups = computed<NavGroup[]>(() => {
    const userRoles = this._userRoles();

    return navigation
      .filter((group) => this._hasAccess(group.roles, userRoles))
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => this._hasAccess(item.roles, userRoles)),
      }))
      .filter((group) => group.items.length > 0);
  });

  ngOnInit(): void {
    const roles = this.keycloakService.getUserRoles();
    this._userRoles.set(roles);
  }

  isOpen(title: string): boolean {
    return this._openItems().has(title);
  }

  toggleItem(title: string): void {
    this._openItems.update((set) => {
      const next = new Set(set);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  }

  /**
   * Devuelve true si el usuario tiene al menos uno de los roles requeridos,
   * o si no se definieron roles (acceso libre para autenticados).
   */
  private _hasAccess(requiredRoles: string[] | undefined, userRoles: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.some((role) => userRoles.includes(role));
  }
}
